import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import { Menu, X, ChevronDown, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { scrollHomeSectionIntoView, HOME_SCROLL_TARGET_KEY } from '@/lib/scrollHomeSection';
import { getWsjPedidosAdminUrl } from '@/lib/wsjPedidosUrls';

const Header = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    scrollHomeSectionIntoView(id);
    setIsMobileMenuOpen(false);
  };

  const goToSection = (id) => {
    if (location.pathname === '/') {
      scrollToSection(id);
      return;
    }
    sessionStorage.setItem(HOME_SCROLL_TARGET_KEY, id);
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const goHomeTop = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { key: 'about', id: 'about' },
    { key: 'methodology', id: 'pyramid' },
    { key: 'founder', id: 'founder' },
    { key: 'contact', id: 'contact' }
  ];

  const sn = t.servicesNav;
  const primaryServiceLinks = [
    { key: 'webDev', label: sn.webDev, action: () => { navigate('/desenvolvimento-web'); setIsMobileMenuOpen(false); } },
    { key: 'ecommerce', label: sn.ecommerce, action: () => { navigate('/ecommerce'); setIsMobileMenuOpen(false); } },
    { key: 'marketing', label: sn.marketing, action: () => { navigate('/marketing-digital'); setIsMobileMenuOpen(false); } },
    { key: 'automation', label: sn.automation, action: () => { navigate('/automacao'); setIsMobileMenuOpen(false); } },
    { key: 'mobile', label: sn.mobile, action: () => { navigate('/aplicativos-mobile'); setIsMobileMenuOpen(false); } },
    { key: 'consulting', label: sn.consulting, action: () => { navigate('/consultoria-ti'); setIsMobileMenuOpen(false); } }
  ];
  const secondaryServiceLinks = [
    { key: 'wsjPedidos', label: sn.wsjPedidos, action: () => { navigate('/wsj-pedidos'); setIsMobileMenuOpen(false); } },
    { key: 'digitalMenu', label: sn.digitalMenu, action: () => { navigate('/wsj-pedidos'); setIsMobileMenuOpen(false); } },
    { key: 'urbanTransport', label: sn.urbanTransport, action: () => { navigate('/transporte-urbano'); setIsMobileMenuOpen(false); } },
    { key: 'digitalInvites', label: sn.digitalInvites, action: () => { navigate('/convites-digitais'); setIsMobileMenuOpen(false); } },
    { key: 'ambassadors', label: sn.ambassadors, action: () => { navigate('/indique-e-ganhe'); setIsMobileMenuOpen(false); } }
  ];
  const mobileServiceLinks = [...primaryServiceLinks, ...secondaryServiceLinks];
  const adminPanelUrl = getWsjPedidosAdminUrl();

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 w-full min-w-0 max-w-full overflow-x-hidden transition-all duration-300 ${
        isScrolled ? 'glass-premium backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20 gap-2">
          <Link
            to="/"
            onClick={(e) => {
              if (location.pathname === '/') {
                e.preventDefault();
                goHomeTop();
              }
            }}
            className="flex items-center cursor-pointer group"
          >
            <div className="text-xl sm:text-2xl font-black tracking-tight flex items-center">
              <span className="text-white">WSJ</span>
              <span className="text-red-600 ml-1 text-2xl sm:text-3xl">.</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center space-x-8">
            <button
              type="button"
              onClick={() => goToSection('about')}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors relative group"
            >
              {t.nav.about}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-500 group-hover:w-full transition-all duration-300" />
            </button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="text-sm font-medium text-white/70 hover:text-white transition-colors relative group inline-flex items-center gap-1 outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40 rounded-sm"
                >
                  {t.nav.services}
                  <ChevronDown className="w-4 h-4 opacity-70" aria-hidden />
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-500 group-hover:w-full transition-all duration-300 group-data-[state=open]:w-full" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={10}
                className="w-[min(calc(100vw-2rem),22rem)] sm:w-[min(28rem,calc(100vw-2rem))] border border-white/10 bg-slate-950/95 text-white backdrop-blur-xl p-2 shadow-2xl"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0.5">
                  {primaryServiceLinks.map((item) => (
                    <DropdownMenuItem
                      key={item.key}
                      onClick={() => item.action()}
                      className="cursor-pointer rounded-lg text-white/90 focus:bg-white/10 focus:text-white"
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </div>
                <DropdownMenuSeparator className="bg-white/10 my-2" />
                <div className="flex flex-col gap-0.5">
                  {secondaryServiceLinks.map((item) => (
                    <DropdownMenuItem
                      key={item.key}
                      onClick={() => item.action()}
                      className="cursor-pointer rounded-lg text-white/90 focus:bg-white/10 focus:text-white"
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {navItems
              .filter((item) => item.key !== 'about')
              .map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => goToSection(item.id)}
                  className="text-sm font-medium text-white/70 hover:text-white transition-colors relative group"
                >
                  {t.nav[item.key]}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-yellow-500 group-hover:w-full transition-all duration-300" />
                </button>
              ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Button
              asChild
              size="sm"
              className="hidden lg:inline-flex rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold shadow-md shadow-amber-900/20 hover:from-amber-400 hover:to-amber-500 px-4 h-9 shrink-0"
            >
              <a
                href={adminPanelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5"
              >
                <LayoutDashboard className="w-4 h-4" aria-hidden />
                {sn.panelCta}
              </a>
            </Button>
            <LanguageSelector />

            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-white hover:bg-white/10 shrink-0"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden py-6 border-t border-white/10 glass-premium backdrop-blur-xl rounded-b-2xl max-h-[min(70vh,calc(100dvh-5rem))] overflow-y-auto">
            <nav className="flex flex-col space-y-1 px-2">
              <Button
                asChild
                className="lg:hidden mx-2 mb-1 rounded-xl w-[calc(100%-1rem)] bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-semibold h-11"
              >
                <a
                  href={adminPanelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2"
                >
                  <LayoutDashboard className="w-4 h-4" aria-hidden />
                  {sn.panelCta}
                </a>
              </Button>

              <div className="px-4 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-white/35">
                {t.nav.services}
              </div>
              {mobileServiceLinks.map((item) => (
                <React.Fragment key={item.key}>
                  {item.key === 'wsjPedidos' ? (
                    <div className="h-px bg-white/10 my-2 mx-2" aria-hidden />
                  ) : null}
                  <button
                    type="button"
                    onClick={item.action}
                    className="text-left text-sm font-medium text-white/80 hover:text-white transition-colors px-4 py-2.5 hover:bg-white/5 rounded-lg pl-6 w-full"
                  >
                    {item.label}
                  </button>
                </React.Fragment>
              ))}

              {navItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => goToSection(item.id)}
                  className="text-left text-sm font-medium text-white/70 hover:text-white transition-colors px-4 py-3 hover:bg-white/5 rounded-lg"
                >
                  {t.nav[item.key]}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
